import { Action } from 'redux'

import { createAsyncAction } from 'Helpers/redux'
import { APP } from 'Configs/App'
import {
  CampaignPlatformTypeEnum,
  SpotifyCampaign,
  TiktokCampaign,
  SpotifyTrack,
  GetGtmInfoInterface,
  CampaignToSchedule,
  User,
  RelaunchedCampaign,
  CampaignDetailsData,
  Payment,
} from 'Types/index'
import {
  sendPaymentCompletedEvent,
  sendPaymentInfoEvent,
} from 'Redux/actions/googleAnalyticsActions'
import {
  paymentCompletedEvent,
  PurchaseTypeEnum,
  purchaseEvent,
} from 'Helpers/tagManager'
import { PaymentMethodEnum, Roles } from 'Constants/enums'
import { apiCall } from './api'

export const ADD_NEW_CAMPAIGN = createAsyncAction('ADD_NEW_CAMPAIGN')
export const addNewCampaign = (
  trackName: string,
  artist: string,
  trackURL: string,
  trackImage: string,
  genres: Array<number>,
  artistURL: string,
  spotify_id: string,
  artist_spotify_id: string | number,
  track_length: number,
  language_ids: Array<number>,
  mood_ids: Array<number>,
  url: string,
  callback: (error: string, response: SpotifyCampaign) => void
) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign',
    query: {
      track_name: trackName,
      artist_name: artist,
      track_url: trackURL,
      track_image: trackImage,
      genre_ids: genres,
      artist_url: artistURL,
      spotify_id,
      artist_spotify_id,
      track_length,
      language_ids,
      mood_ids,
      url,
    },
    types: ADD_NEW_CAMPAIGN,
    callback,
  })

export const ADD_NEW_NUMBER = createAsyncAction('ADD_NEW_NUMBER')
export const addNewNumber = (phone_number: string, callback: () => void) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/update-phone-number',
    query: {
      phone_number,
    },
    types: ADD_NEW_NUMBER,
    callback,
  })

export const UPDATE_NUMBER_FROM_STATE = 'UPDATE_NUMBER_FROM_STATE'
export const updateNumberFromState = (phone_number: string) => ({
  payload: phone_number,
  type: UPDATE_NUMBER_FROM_STATE,
})

export const GET_ALL_CAMPAIGNS = 'GET_ALL_CAMPAIGNS'
// @ts-ignore
export const getAllCampaigns = campaigns => ({
  payload: campaigns,
  type: GET_ALL_CAMPAIGNS,
})

export const GET_CAMPAIGNS_LIST = createAsyncAction('GET_CAMPAIGNS_LIST')
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCampaignsList = (callback?: any) =>
  apiCall({
    method: 'get',
    endpoint: `/campaign`,
    types: GET_CAMPAIGNS_LIST,
    callback,
  })

export const SCHEDULE_CAMPAIGN = createAsyncAction('SCHEDULE_CAMPAIGN')
export const scheduleCampaign = (
  campaign_id: number,
  start_date: string,
  end_date: string,
  coupon_id: number,
  current_balance: number,
  balance_used: number,
  callback: () => void
) =>
  apiCall({
    method: 'put',
    endpoint: '/campaign',
    query: {
      campaign_id,
      start_date,
      end_date,
      coupon_id,
      current_balance,
      balance_used,
    },
    types: SCHEDULE_CAMPAIGN,
    callback,
  })

export const RENEW_CAMPAIGN = createAsyncAction('RENEW_CAMPAIGN')
export const renewCampaign = (
  campaign_id: number,
  start_date: string,
  end_date: string,
  curator_ids: Array<number>,
  isPaypalPayment: boolean,
  transaction_id: string,
  amount: number,
  created_at: string,
  country: string,
  coupon_id: number,
  current_balance: number,
  balance_used: number,
  callback: () => void
) =>
  apiCall({
    method: 'put',
    endpoint: '/campaign/renew',
    query: {
      campaign_id,
      start_date,
      end_date,
      curator_ids,
      isPaypalPayment,
      transaction_id,
      amount,
      created_at,
      country,
      coupon_id,
      current_balance,
      balance_used,
    },
    types: RENEW_CAMPAIGN,
    callback,
  })

export const CHECK_TRACK_TO_CREATE_CAMPAIGN = createAsyncAction(
  'CHECK_TRACK_TO_CREATE_CAMPAIGN'
)
export const checkTrackToCreateCampaign = (
  trackURL: string,
  callback: (error: Error) => void
) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/check-track',
    query: {
      track_url: trackURL,
    },
    types: CHECK_TRACK_TO_CREATE_CAMPAIGN,
    callback,
  })

export const CHECK_ALBUM_TO_CREATE_CAMPAIGN = createAsyncAction(
  'CHECK_ALBUM_TO_CREATE_CAMPAIGN'
)
export const checkAlbumToCreateCampaign = (
  album_id: string,
  callback: (error: Error) => void
) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/check-album',
    query: {
      album_id,
    },
    types: CHECK_ALBUM_TO_CREATE_CAMPAIGN,
    callback,
  })

export const GET_CAMPAIGN_DETAILS = createAsyncAction('GET_CAMPAIGN_DETAILS')
export const getCampaignDetails = (
  campaign_id: number,
  callback?: (error: string, res: CampaignDetailsData) => void
) =>
  apiCall({
    method: 'get',
    endpoint: `/campaign/details/${campaign_id}`,
    types: GET_CAMPAIGN_DETAILS,
    callback,
  })

export const GET_CAMPAIGN_RATES = createAsyncAction('GET_CAMPAIGN_RATES')
export const getCampaignRates = () =>
  apiCall({
    method: 'get',
    endpoint: `/campaign/rates`,
    types: GET_CAMPAIGN_RATES,
  })

export const GET_CAMPAIGN_REVIEWS = createAsyncAction('GET_CAMPAIGN_REVIEWS')
export const getCampaignReviews = (
  campaign_id: number,
  start = 0,
  limit = 10,
  active = 'allResponses'
) =>
  apiCall({
    method: 'get',
    endpoint: `/campaign/reviews/${campaign_id}?start=${start}&limit=${limit}&active=${active}`,
    types: GET_CAMPAIGN_REVIEWS,
  })

export const GET_CAMPAIGN_RESULTS = createAsyncAction('GET_CAMPAIGN_RESULTS')
export const getCampaignResults = (campaign_id: number) =>
  apiCall({
    method: 'get',
    endpoint: `/campaign/results/${campaign_id}`,
    types: GET_CAMPAIGN_RESULTS,
  })

export const GET_CAMPAIGN_STATUS = createAsyncAction('GET_CAMPAIGN_STATUS')
export const getCampaignStatus = (
  campaign_id: number,
  callback: (
    err: string,
    res: { campaign: SpotifyCampaign; payment: Payment }
  ) => void
) =>
  apiCall({
    method: 'get',
    endpoint: `/campaign/status/${campaign_id}`,
    types: GET_CAMPAIGN_STATUS,
    callback,
  })

export const GET_PRESCHEDULE_PRICE = createAsyncAction('GET_PRESCHEDULE_PRICE')
export const getPreschedulePrice = (genre_ids: Array<number>) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/curators-list',
    query: {
      genre_ids,
    },
    types: GET_PRESCHEDULE_PRICE,
  })

export const SET_PAYPAL_PAYMENT = createAsyncAction('SET_PAYPAL_PAYMENT')
export const setPaypalPayment = (
  transactionId: string,
  amount: number,
  createdAt: string,
  country: string,
  campaigns: {
    type: CampaignPlatformTypeEnum
    genres: string[]
    languages: string[]
    startDate: string
    isDanceRequested?: boolean
    comment?: string
    campaignId: number
    amount: number
  }[],
  couponId: number,
  callback: (err: string) => void
) =>
  apiCall({
    method: 'post',
    endpoint: `${APP.TIKTOK_SERVER}/payment/paypal/charge`,
    query: {
      campaigns: [
        {
          type: campaigns[0].type,
          genres: campaigns[0].genres,
          languages: campaigns[0].languages,
          amount: campaigns[0].amount,
          startDate: campaigns[0].startDate,
          isDanceRequested: campaigns[0].isDanceRequested,
          comment: campaigns[0].comment,
          campaignId: campaigns[0].campaignId,
        },
      ],
      amount,
      country,
      createdAt,
      transactionId,
      couponId,
    },
    types: SET_PAYPAL_PAYMENT,
    callback,
  })

export const SET_TIKTOK_VALUE_TO_WEBHOOK = createAsyncAction(
  'SET_TIKTOK_VALUE_TO_WEBHOOK'
)
export const setTiktokValueToWebhook = (
  first_name: string,
  last_name: string,
  email: string
) =>
  apiCall({
    method: 'post',
    endpoint: 'https://hook.integromat.com/g3xiddgcj9ajegpw8l4xtdmgobi7ppl6',
    query: {
      first_name,
      last_name,
      email,
    },
    types: SET_TIKTOK_VALUE_TO_WEBHOOK,
  })

export const CREATE_CAMPAIGN_WITHOUT_SIGNUP = createAsyncAction(
  'CREATE_CAMPAIGN_WITHOUT_SIGNUP'
)
export const createCampaignWithoutSignup = (
  formStates: {
    email: string
    ip: string
    firstName: string
    lastName: string
    phone_number: string
  },
  spotifyTrack: SpotifyTrack,
  genre_ids: Array<number>,
  language_ids: Array<number>,
  mood_ids: Array<number>,
  gcid: string,
  utmObj: unknown,
  url: string,
  callback: () => void,
  referral_code: string
) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/create',
    query: {
      artist: formStates,
      gcid,
      utmObj,
      referral_code,
      spotify_campaign: {
        genre_ids,
        language_ids,
        mood_ids,
        url,
        spotify_track: spotifyTrack,
      },
    },
    types: CREATE_CAMPAIGN_WITHOUT_SIGNUP,
    callback,
  })

export const GET_ARTIST_SPOTIFY_CONNECTIONS = createAsyncAction(
  'GET_ARTIST_SPOTIFY_CONNECTIONS'
)
export const getArtistSpotifyConnections = () =>
  apiCall({
    method: 'get',
    endpoint: `/artist/spotify-connections`,
    types: GET_ARTIST_SPOTIFY_CONNECTIONS,
  })

export const SET_SPOTIFY_CONNECTION_FOR_ARTIST = createAsyncAction(
  'SET_SPOTIFY_CONNECTION_FOR_ARTIST'
)
export const setSpotifyConnectionForArtist = (
  artist_spotify_id: string,
  callback: (error: string) => void
) =>
  apiCall({
    method: 'post',
    endpoint: '/artist/spotify-connection',
    query: {
      artist_spotify_id,
    },
    types: SET_SPOTIFY_CONNECTION_FOR_ARTIST,
    callback,
  })

export const CREATE_RELAUNCH_CAMPAIGN = createAsyncAction('RELAUNCH_CAMPAIGN')
export const createRelaunchCampaign = (
  campaign_id: number | string,
  url: string,

  callback: (error: string, res: RelaunchedCampaign) => void
) =>
  apiCall({
    method: 'post',
    endpoint: '/campaign/relaunch',
    query: {
      campaign_id,
      url,
    },
    types: CREATE_RELAUNCH_CAMPAIGN,
    callback,
  })

export const ADD_NEW_TIK_TOK_CAMPAIGN = createAsyncAction(
  'ADD_NEW_TIK_TOK_CAMPAIGN'
)

export const addNewTikTokCampaign = (
  {
    // @ts-ignore
    trackUrl,
    // @ts-ignore
    genres,
    // @ts-ignore
    languages,
    // @ts-ignore
    trackTiktokId,
    // @ts-ignore
    trackTitle,
    // @ts-ignore
    trackPlayUrl,
    // @ts-ignore
    trackCoverUrl,
    // @ts-ignore
    trackAuthorName,
    // @ts-ignore
    initialVideoCount,
    // @ts-ignore
    trackDuration,
  },
  // @ts-ignore
  callback
) =>
  apiCall({
    method: 'post',
    endpoint: `${APP.TIKTOK_SERVER}/artist/tiktok/campaigns`,
    query: {
      trackUrl,
      genres,
      languages,
      trackTiktokId,
      trackTitle,
      trackPlayUrl,
      trackCoverUrl,
      trackAuthorName,
      initialVideoCount,
      trackDuration,
    },
    types: ADD_NEW_TIK_TOK_CAMPAIGN,
    callback,
  })

export const GET_CAMPAIGNS_TIKTOK_LIST = createAsyncAction(
  'GET_CAMPAIGNS_TIKTOK_LIST'
)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCampaignsTiktokList = (callback?: any) =>
  apiCall({
    method: 'get',
    endpoint: `${APP.TIKTOK_SERVER}/artist/tiktok/campaigns`,
    types: GET_CAMPAIGNS_TIKTOK_LIST,
    callback,
  })

export const GET_TIKTOK_CAMPAIGN_EVENTS_STATUSES = createAsyncAction(
  'GET_TIKTOK_CAMPAIGN_EVENTS_STATUSES'
)
export const getTiktokCampaignEventsStatus = (
  tiktokCampaignId: string | number
) =>
  apiCall({
    method: 'get',
    endpoint: `${APP.TIKTOK_SERVER}/tiktok-campaigns/${tiktokCampaignId}/events/statuses`,
    types: GET_TIKTOK_CAMPAIGN_EVENTS_STATUSES,
  })

export const GET_TIKTOK_CAMPAIGN_DETAILS = createAsyncAction(
  'GET_TIKTOK_CAMPAIGN_DETAILS'
)
export const getTiktokCampaignDetails = (tiktokCampaignId: string | number) =>
  apiCall({
    method: 'get',
    endpoint: `${APP.TIKTOK_SERVER}/tiktok-campaigns/${tiktokCampaignId}/details`,
    types: GET_TIKTOK_CAMPAIGN_DETAILS,
  })

export const GET_TIKTOK_CAMPAIGN_EVENTS = createAsyncAction(
  'GET_TIKTOK_CAMPAIGN_EVENTS'
)
export const getTiktokCampaignEvents = (
  tiktokCampaignId: string | number,
  status = '',
  pageNumber = 1
) =>
  apiCall({
    method: 'get',
    endpoint: `${
      APP.TIKTOK_SERVER
    }/tiktok-campaigns/${tiktokCampaignId}/events?${status &&
      `status=${status}`}&pageNumber=${pageNumber}`,
    types: GET_TIKTOK_CAMPAIGN_EVENTS,
  })

export const POST_TIKTOK_CAMPAIGN_EVENT_REJECT = createAsyncAction(
  'POST_TIKTOK_CAMPAIGN_EVENT_REJECT'
)
export const postTiktokCampaignReject = (
  tiktokCampaignEventId: number,
  feedback: string
) =>
  apiCall({
    method: 'post',
    endpoint: `${APP.TIKTOK_SERVER}/artist/tiktok/reject-creator/${tiktokCampaignEventId}`,
    query: {
      feedback,
    },
    types: POST_TIKTOK_CAMPAIGN_EVENT_REJECT,
  })

export const PUT_TIKTOK_CAMPAIGN_EVENT_RATE = createAsyncAction(
  'PUT_TIKTOK_CAMPAIGN_EVENT_RATE'
)
export const putTiktokCampaignRate = (
  tiktokCampaignEventId: number,
  feedback: string,
  stars: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (error: string, res: any) => void
) =>
  apiCall({
    method: 'put',
    endpoint: `${APP.TIKTOK_SERVER}/artist/tiktok/rate-video/${tiktokCampaignEventId}`,
    query: {
      feedback,
      stars,
    },
    types: PUT_TIKTOK_CAMPAIGN_EVENT_RATE,
    callback,
  })

export const PUT_TIKTOK_CAMPAIGN_EVENT_VIDEO = createAsyncAction(
  'PUT_TIKTOK_CAMPAIGN_EVENT_VIDEO'
)
export const putTiktokCampaignVideo = (tiktokCampaignEventId: string) =>
  apiCall({
    method: 'put',
    endpoint: `${APP.TIKTOK_SERVER}/tiktok-campaigns/events/${tiktokCampaignEventId}/video`,
    types: PUT_TIKTOK_CAMPAIGN_EVENT_VIDEO,
  })

export const GET_CAMPAIGNS = 'GET_CAMPAIGNS'
export const getCampaigns = () => ({
  type: GET_CAMPAIGNS,
})

export const SET_CAMPAIGNS = 'SET_CAMPAIGNS'
export const setCampaigns = (
  payload: Array<TiktokCampaign | SpotifyCampaign>
) => ({
  type: SET_CAMPAIGNS,
  payload,
})

export const GET_GTM_PAYMENT_INFO = createAsyncAction('GET_GTM_PAYMENT_INFO')
export const getGtmPaymentInfo = (
  campaignId: number | string,
  callback?: (error: string, res: GetGtmInfoInterface) => void
) =>
  apiCall({
    method: 'get',
    endpoint: `${APP.TIKTOK_SERVER}/analytic/payment-gtm/${campaignId}`,
    types: GET_GTM_PAYMENT_INFO,
    callback,
  })

// GTM Thunk

export const sendStripePaymentGTMThunk: ({
  campaignId,
  resultType,
  userId,
  userRole,
  campaignDetails,
  user,
}: {
  campaignId: number | string
  resultType: 'success' | 'failure'
  userId: number | string
  userRole: Roles
  campaignDetails: CampaignToSchedule
  user: User
}) => (
  dispatch: (arg0: Action) => Promise<{ payload: GetGtmInfoInterface }>
) => void = ({
  campaignId,
  resultType,
  userId,
  userRole,
  campaignDetails,
  user,
}: {
  campaignId: number | string
  resultType: 'success' | 'failure'
  userId: number | string
  userRole: Roles
  campaignDetails: CampaignToSchedule
  user: User
}) => (
  dispatch: (arg0: Action) => Promise<{ payload: GetGtmInfoInterface }>
) => {
  dispatch(getGtmPaymentInfo(campaignId)).then(
    ({ payload }: { payload: GetGtmInfoInterface }) => {
      if (!payload || !payload.campaignId) return
      if (resultType === 'success') {
        // payment complete GTM
        paymentCompletedEvent({
          campaignId,
          campaignNumber: payload.campaignNumber,
          campaignPlatformType: CampaignPlatformTypeEnum.SPOTIFY,
          campaignType: payload.campaignType,
          campaignValue: payload.resultPrice,
          genresList: payload.genresList.join(),
          languagesList: payload.languagesList.join(),
          paymentAmount: payload.value,
          paymentMethod: PaymentMethodEnum.Stripe,
          purchaseType: payload.purchaseType as PurchaseTypeEnum,
          transactionId: payload.transactionId,
          userId,
          userRole,
          is_feed: payload.isFeed,
        })
        purchaseEvent({
          user,
          campaignId,
          transactionTotal: payload.value,
          price: payload.resultPrice,
          isFirstCampaign: payload.campaignType === 'first',
        })
        dispatch(
          sendPaymentCompletedEvent(
            campaignDetails.id,
            {
              price: campaignDetails?.price,
              start_date: campaignDetails.start_date,
            },
            window.location.href
          )
        )
        dispatch(sendPaymentInfoEvent(campaignDetails.id, window.location.href))
      }
    }
  )
}
